/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Container, Segment, Header, Message, Loader, Dimmer, Icon, Input, List } from 'semantic-ui-react';
import { apiPath } from '@aws-ee/base-ui/dist/helpers/settings';

// Stanford cardinal.
const CARDINAL = '#8c1515';

function parseLocation() {
  const path = window.location.pathname || '';
  const match = path.match(/\/verify-certificate\/([^/?#]+)/);
  const id = match ? decodeURIComponent(match[1]) : '';
  const params = new URLSearchParams(window.location.search || '');
  return { id, sig: params.get('sig') || '' };
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (e) {
    return iso;
  }
}

/**
 * Public, unauthenticated certificate verification page. Rendered by the app-component plugin for
 * /verify-certificate/* paths, so it never sees the app's auth gate. Self-contained: reads the id + sig
 * from the URL and calls the public verify API, which returns only the closed, public-safe view.
 */
class VerifierApp extends React.Component {
  constructor(props) {
    super(props);
    const { id, sig } = parseLocation();
    this.state = { id, sig, loading: Boolean(id), result: null, error: null, manual: '' };
  }

  componentDidMount() {
    document.title = 'Verify Certificate — Stanford Data Ocean';
    if (this.state.id) this.verify(this.state.id, this.state.sig);
  }

  async verify(id, sig) {
    this.setState({ loading: true, error: null, result: null });
    try {
      const url = `${apiPath}/api/certificates/public/verify/${encodeURIComponent(id)}?sig=${encodeURIComponent(sig || '')}`;
      const res = await fetch(url, { mode: 'cors' });
      const result = await res.json();
      this.setState({ loading: false, result });
    } catch (err) {
      this.setState({ loading: false, error: 'We could not reach the verification service. Please try again.' });
    }
  }

  handleManual = () => {
    const input = (this.state.manual || '').trim();
    if (!input) return;
    // Accept either a pasted verification link or a bare certificate id.
    let id = input;
    let sig = '';
    const match = input.match(/verify-certificate\/([^/?#]+)/);
    if (match) {
      id = decodeURIComponent(match[1]);
      const sigMatch = input.match(/[?&]sig=([^&]+)/);
      if (sigMatch) sig = decodeURIComponent(sigMatch[1]);
    }
    const newUrl = `/verify-certificate/${encodeURIComponent(id)}${sig ? `?sig=${encodeURIComponent(sig)}` : ''}`;
    window.history.replaceState(null, '', newUrl);
    this.setState({ id, sig });
    this.verify(id, sig);
  };

  renderResult() {
    const { result } = this.state;
    if (!result) return null;

    if (result.valid && result.status === 'ISSUED') {
      return (
        <Message positive icon>
          <Icon name="check circle" />
          <Message.Content>
            <Message.Header>Valid certificate</Message.Header>
            <List relaxed style={{ marginTop: '0.75rem' }}>
              <List.Item>
                <strong>Issued to:</strong> {result.issuedTo}
              </List.Item>
              <List.Item>
                <strong>Program:</strong> {result.program}
              </List.Item>
              <List.Item>
                <strong>Type:</strong> {result.certificateType}
              </List.Item>
              <List.Item>
                <strong>Issued:</strong> {formatDate(result.issuedAt)}
              </List.Item>
              <List.Item>
                <strong>Certificate ID:</strong> {result.certificateId}
              </List.Item>
              <List.Item>
                <strong>Issuer:</strong> {result.issuer}
              </List.Item>
            </List>
          </Message.Content>
        </Message>
      );
    }

    if (result.status === 'REVOKED') {
      return (
        <Message negative icon>
          <Icon name="ban" />
          <Message.Content>
            <Message.Header>Revoked</Message.Header>
            <p>This certificate has been revoked and is no longer valid.</p>
          </Message.Content>
        </Message>
      );
    }

    if (result.status === 'SUPERSEDED') {
      return (
        <Message warning icon>
          <Icon name="history" />
          <Message.Content>
            <Message.Header>Superseded</Message.Header>
            <p>This certificate has been replaced by a newer version.</p>
          </Message.Content>
        </Message>
      );
    }

    // NOT_FOUND (also covers a bad or missing signature — deliberately indistinguishable).
    return (
      <Message negative icon>
        <Icon name="question circle outline" />
        <Message.Content>
          <Message.Header>Not found</Message.Header>
          <p>We could not verify this certificate. Please check the verification link or certificate ID.</p>
        </Message.Content>
      </Message>
    );
  }

  render() {
    const { loading, error, id, result, manual } = this.state;
    return (
      <Container text style={{ marginTop: '3rem', marginBottom: '3rem' }}>
        <Segment.Group raised>
          <Segment style={{ background: CARDINAL, color: 'white' }}>
            <Header as="h2" inverted style={{ marginBottom: '0.25rem' }}>
              Stanford Data Ocean
            </Header>
            <div>Certificate Verification</div>
          </Segment>

          <Segment style={{ minHeight: '6rem' }}>
            {loading && (
              <Dimmer active inverted>
                <Loader inverted>Verifying…</Loader>
              </Dimmer>
            )}
            {!loading && error && (
              <Message negative>
                <Message.Header>Something went wrong</Message.Header>
                <p>{error}</p>
              </Message>
            )}
            {!loading && !error && this.renderResult()}
            {!loading && !error && !id && !result && (
              <p>Enter a certificate ID or paste a verification link below to check whether a certificate is genuine.</p>
            )}
          </Segment>

          <Segment secondary>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Verify a certificate
            </label>
            <Input
              fluid
              placeholder="Certificate ID or verification link"
              value={manual}
              onChange={(e, data) => this.setState({ manual: data.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') this.handleManual();
              }}
              action={{ content: 'Verify', color: 'red', onClick: this.handleManual }}
            />
          </Segment>
        </Segment.Group>
      </Container>
    );
  }
}

export default VerifierApp;
